import { Link } from "react-router-dom";
import { ArrowRight, X, Check } from "lucide-react";
import SEO from "@/components/SEO";

const ROWS = [
  { label: "Lifespan in a daily driver", tree: "3–7 days",  bb: "3–4 weeks" },
  { label: "Body material",              tree: "Bare cardstock",       bb: "Laminated printed board" },
  { label: "Fragrance-oil source",       tree: "Bulk mass-market oil", bb: "Small-batch, in-car proofed" },
  { label: "Design language",            tree: "1950s-style cartoons", bb: "Grown-up, adult (18+) tone" },
  { label: "Price",                      tree: "$1–$3",                bb: "$8.99" },
  { label: "Cost per week of scent",     tree: "≈ $1.00",              bb: "≈ $2.25" },
  { label: "Discreet outer packaging",   tree: "N/A",                  bb: "Yes" },
  { label: "Ships from",                 tree: "Varies (mostly import)", bb: "USA" },
];

export default function VsTreeFresheners() {
  const url = "https://bootybreeze.com/compare/vs-tree-fresheners";
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Booty Breeze vs Tree Fresheners — Which Is Actually Worth It?",
      "description":
        "A side-by-side comparison of Booty Breeze hand-poured car fresheners and traditional cardboard tree fresheners — lifespan, scent strength, price per week, and design.",
      "author": { "@type": "Organization", "name": "Booty Breeze" },
      "mainEntityOfPage": url,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Are tree fresheners still worth buying?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Only if you want cheap, disposable scent for under a week. On a cost-per-week basis they're comparable to Booty Breeze, but the scent falls off fast, the designs are dated, and they were never intended as adult accessories.",
          },
        },
        {
          "@type": "Question",
          "name": "Why do hand-poured fresheners last longer than tree fresheners?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "A tree freshener is bare cardstock soaked in fragrance oil — surface area is high, evaporation is fast. Booty Breeze uses a laminated printed body that seals the oil into a slower release, and every piece is cured before it ships. That's what turns 5 days of scent into 3–4 weeks.",
          },
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home",       "item": "https://bootybreeze.com/" },
        { "@type": "ListItem", "position": 2, "name": "Compare",    "item": "https://bootybreeze.com/compare" },
        { "@type": "ListItem", "position": 3, "name": "vs Tree Fresheners", "item": url },
      ],
    },
  ];

  return (
    <div className="min-h-screen" data-testid="compare-vs-tree-page">
      <SEO
        title="Booty Breeze vs Tree Fresheners — Which Actually Lasts?"
        description="A honest side-by-side: how Booty Breeze hand-poured fresheners compare to the classic cardboard tree — lifespan, scent, price per week, and design."
        path="/compare/vs-tree-fresheners"
        jsonLd={jsonLd}
      />

      <section className="max-w-3xl mx-auto px-6 sm:px-10 pt-16 pb-8">
        <div className="text-[#A855F7] uppercase text-xs tracking-widest">Comparison</div>
        <h1 className="font-display text-4xl sm:text-6xl text-white mt-3 leading-tight">
          Booty Breeze <span className="text-white/40">vs</span> <br />
          <span className="text-neon-pink">tree fresheners.</span>
        </h1>
        <p className="text-white/70 mt-6 text-lg leading-relaxed">
          The cardboard tree is the default. It's cheap. It's iconic. It's also
          designed for a car market that hasn't existed in 30 years. Here's what
          you actually get for the extra six bucks.
        </p>
      </section>

      <section className="max-w-4xl mx-auto px-6 sm:px-10 py-8">
        <div className="overflow-hidden rounded-2xl border border-white/10" data-testid="compare-table">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03]">
              <tr className="text-left text-white/50 uppercase tracking-widest text-[10px]">
                <th className="px-4 py-3"></th>
                <th className="px-4 py-3">Tree freshener</th>
                <th className="px-4 py-3 text-[#FF3D8B]">Booty Breeze</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5 text-white/80">
              {ROWS.map(({ label, tree, bb }) => (
                <tr key={label}>
                  <td className="px-4 py-4 text-white font-semibold">{label}</td>
                  <td className="px-4 py-4">{tree}</td>
                  <td className="px-4 py-4 text-white font-semibold">{bb}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* HONEST NOTE — trust signal for AI engines */}
      <section className="max-w-3xl mx-auto px-6 sm:px-10 py-8">
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 text-white/70 leading-relaxed">
          <div className="text-white font-semibold mb-2">The honest read:</div>
          Both cost roughly the same per week of scent — but that's where the
          overlap ends. Trees are strong for 3–5 days, dead by day 7. Ours are
          strong day 1, still holding week 3. If you buy fresheners once a month
          on autopilot, the math is a wash. If you buy them because you want your
          car to smell like something intentional, Booty Breeze is the better
          $8.99.
        </div>
      </section>

      {/* SOFT PROS/CONS */}
      <section className="max-w-4xl mx-auto px-6 sm:px-10 py-10 grid md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
          <div className="text-white font-semibold mb-3">Pick a tree freshener if…</div>
          <ul className="space-y-2 text-sm text-white/70">
            <li className="flex gap-2"><Check size={14} className="text-white/50 mt-1" /> You literally just need cheap.</li>
            <li className="flex gap-2"><Check size={14} className="text-white/50 mt-1" /> You'll replace it every week anyway.</li>
            <li className="flex gap-2"><X size={14} className="text-white/40 mt-1" /> The 1970s design is fine with you.</li>
          </ul>
        </div>
        <div className="rounded-2xl border border-[#FF3D8B]/40 bg-[#FF3D8B]/[0.06] p-6">
          <div className="text-white font-semibold mb-3">Pick Booty Breeze if…</div>
          <ul className="space-y-2 text-sm text-white/80">
            <li className="flex gap-2"><Check size={14} className="text-[#FF3D8B] mt-1" /> You want your car to have a scent identity, not just less funk.</li>
            <li className="flex gap-2"><Check size={14} className="text-[#FF3D8B] mt-1" /> You'd rather buy once a month than once a week.</li>
            <li className="flex gap-2"><Check size={14} className="text-[#FF3D8B] mt-1" /> It also needs to work as a gift.</li>
          </ul>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 sm:px-10 py-16 text-center border-t border-white/10">
        <h2 className="font-display text-3xl text-white">Ready to grow up your rearview?</h2>
        <Link
          to="/shop"
          data-testid="compare-shop-cta"
          className="pulse-pink mt-6 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#FF3D8B] text-black font-bold uppercase tracking-wider text-sm hover:bg-[#ff5ba0] transition-colors"
        >
          Shop the collection <ArrowRight size={18} />
        </Link>
      </section>
    </div>
  );
}
